import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  position: relative;
  min-height: 700px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  overflow: hidden;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(129, 140, 248, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.1) 0%, transparent 70%);
    opacity: 0.8;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 8rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 4rem;
    padding: 6rem 2rem;
  }
`;

const HeroText = styled.div``;

const MobileBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(129, 140, 248, 0.1);
  color: #818cf8;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(129, 140, 248, 0.2);
  backdrop-filter: blur(10px);
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #818cf8 0%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 2.5rem;
  line-height: 1.7;
  opacity: 0.9;
`;

const MobileDevices = styled(motion.div)`
  position: relative;
  width: 500px;
  height: 500px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(129, 140, 248, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%);
    border-radius: 50%;
    animation: pulse 3s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.2;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.5;
    }
  }

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }
`;

const DeviceIcon = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 180px;
  height: 180px;
  background: linear-gradient(135deg, #818cf8 0%, #a855f7 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 
    0 0 0 20px rgba(129, 140, 248, 0.1),
    0 0 0 40px rgba(129, 140, 248, 0.05),
    0 10px 30px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const FeaturesSection = styled.section`
  padding: 8rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(129, 140, 248, 0.05) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.05) 0%, transparent 70%);
  }
`;

const FeatureGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  display: flex;
  gap: 2rem;
  padding: 2.5rem;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(129, 140, 248, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(129, 140, 248, 0.2);
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(129, 140, 248, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    background: linear-gradient(135deg, rgba(129, 140, 248, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%);
    transform: scale(1.1);
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  font-size: 1.1rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
  
  span {
    background: linear-gradient(135deg, #818cf8 0%, #a855f7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
`;

const CTASection = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(129, 140, 248, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.1) 0%, transparent 70%);
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #818cf8 0%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 3rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto 2rem;
  opacity: 0.9;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #818cf8 0%, #a855f7 100%);
  color: white;
  padding: 1.5rem 4rem;
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
  }
`;

const DeviceTestingSection = styled.section`
  padding: 8rem 2rem;
  background: #f8fafc;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(129, 140, 248, 0.08) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.08) 0%, transparent 70%);
  }
`;

const DeviceGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DeviceCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(129, 140, 248, 0.1);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(129, 140, 248, 0.2);
  }

  .icon-wrapper {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, rgba(129, 140, 248, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 1.5rem;
    transition: all 0.3s ease;
  }

  &:hover .icon-wrapper {
    background: linear-gradient(135deg, rgba(129, 140, 248, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%);
    transform: scale(1.1);
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1rem;
  }

  p {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.6;
  }
`;

function MobileTesting() {
  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Mobile Testing', path: '/services/mobile' }
      ]} />
      <Hero>
        <HeroContent>
          <HeroText>
            <MobileBadge
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              📱 Mobile Testing Solutions
            </MobileBadge>
            <Title
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Perfect Mobile Experience for Every Customer
            </Title>
            <Subtitle>
              Ensure flawless functionality across all mobile devices with our comprehensive
              testing solutions. Deliver an exceptional mobile shopping experience.
            </Subtitle>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Free Trial
            </CTAButton>
          </HeroText>
          <MobileDevices
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.8, 
              delay: 0.4,
              type: "spring",
              stiffness: 100
            }}
          >
            <DeviceIcon
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{
                duration: 1,
                delay: 0.6,
                type: "spring",
                stiffness: 100
              }}
            >
              <svg 
                width="80" 
                height="80" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12" y2="18"/>
              </svg>
            </DeviceIcon>
          </MobileDevices>
        </HeroContent>
      </Hero>

      <FeaturesSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Comprehensive <span>Mobile Testing</span>
          </SectionTitle>
          <SectionSubtitle>
            Test across all mobile devices and scenarios to ensure a perfect shopping experience
          </SectionSubtitle>
        </SectionHeader>
        <FeatureGrid>
          {[
            {
              icon: '📱',
              title: 'Cross-Device Testing',
              description: 'Test your store across all popular mobile devices and screen sizes.'
            },
            {
              icon: '🔄',
              title: 'Responsive Design Testing',
              description: 'Ensure your store looks and works perfectly at every breakpoint.'
            },
            {
              icon: '⚡',
              title: 'Performance Testing',
              description: 'Optimize loading times and performance on mobile networks.'
            },
            {
              icon: '🛒',
              title: 'Mobile Checkout Testing',
              description: 'Verify smooth checkout process on all mobile devices.'
            },
            {
              icon: '📊',
              title: 'Analytics & Reporting',
              description: 'Get detailed insights into mobile user behavior and issues.'
            },
            {
              icon: '🔍',
              title: 'UX Testing',
              description: 'Ensure intuitive navigation and interaction on mobile devices.'
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeaturesSection>

      <DeviceTestingSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Supported <span>Devices</span>
          </SectionTitle>
          <SectionSubtitle>
            Test your store across all major mobile devices and operating systems
          </SectionSubtitle>
        </SectionHeader>
        <DeviceGrid>
          {[
            {
              icon: '📱',
              name: 'iOS Devices',
              description: 'iPhone, iPad, and iPod Touch testing across all versions'
            },
            {
              icon: '🤖',
              name: 'Android Devices',
              description: 'Testing across all major Android phones and tablets'
            },
            {
              icon: '💻',
              name: 'Tablets',
              description: 'Comprehensive testing for all tablet form factors'
            },
            {
              icon: '🌐',
              name: 'Mobile Browsers',
              description: 'Testing across Safari, Chrome, Firefox, and more'
            }
          ].map((device, index) => (
            <DeviceCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="icon-wrapper">{device.icon}</div>
              <h3>{device.name}</h3>
              <p>{device.description}</p>
            </DeviceCard>
          ))}
        </DeviceGrid>
      </DeviceTestingSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Perfect Your Mobile Experience?</CTATitle>
          <CTAText>
            Start testing your store across all mobile devices and ensure a flawless shopping experience for every customer.
          </CTAText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started Free
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default MobileTesting;